// ============================================================================
// DataTable — portage du composant de la maquette (apps/console/design/DataTable.dc.html) :
// carte GLASS + barre de tête (titre · recherche à loupe · compteur mono) + en-têtes TRIABLES
// (⇅ / ▲ / ▼ teal) + pagination (plage, sélecteur 10/25/50, ← →) + empty states (vide ≠ recherche
// sans résultat, avec « Effacer la recherche »). Générique : les cellules sont des nœuds libres ; une ligne porte
// ses ACTIONS (boutons texte, boutons icône, places gardées — recette unique de la maquette) ou un `trailing` libre.
// ============================================================================
import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { EmptyState, Icon, type IconName } from '@orgazone/ui-web';
import { TABLE } from '@orgazone/ui-tokens';
import { TableScroll } from './table-scroll';

export interface DtCol {
  key: string;
  label: string;
  /** Colonne triable : fournir `sortValue` sur les cellules (sinon tri sur le texte `text`). */
  sortable?: boolean;
  /** Largeur sous laquelle la colonne ne se comprime pas (maquette `minWidth`). */
  minWidth?: number;
  /**
   * Largeur au-delà de laquelle la valeur PRINCIPALE se coupe en « … », avec la valeur entière en infobulle (maquette
   * `maxWidth`) : un nom long ne déforme plus la colonne. ⚠️ La valeur seule : les sous-lignes (série, identifiant,
   * type « sans type d'équipement ») ne se coupent jamais — ce sont elles qu'on vérifie.
   */
  maxWidth?: number;
  /**
   * Les sous-lignes passent à la ligne (maquette `wrap`) : une raison d'état ou une liste de versions se lit sur deux lignes
   * plutôt que d'élargir la colonne jusqu'à pousser les autres. ⚠️ Par défaut elles ne se coupent pas — une série, un
   * identifiant se vérifient d'un seul tenant.
   */
  wrap?: boolean;
}

export interface DtCell {
  /** Rendu libre (texte, pastille, tuile+nom…). */
  v: ReactNode;
  /** Sous-ligne mono 10.5 (GLN, EAN, méta) — anatomie maquette. */
  sub?: ReactNode;
  /** Troisième ligne, en texte courant (le type et le modèle d'un appareil, sous sa série) — anatomie maquette. */
  sub2?: ReactNode;
  /** Valeur de tri (numérique ou texte) ; défaut = `text`. */
  sortValue?: number | string;
  /** Texte BRUT de la cellule pour la recherche et le tri par défaut. */
  text?: string;
  /** Ton de la sous-ligne (échéance dépassée, proche…) : elle passe en couleur d'état et en gras. */
  subTone?: 'ok' | 'wa' | 'da' | 'in' | 'ne' | 'vi';
  /** Pastille SOUS le texte (l'état d'un appareil sous son magasin) — maquette `tag`, 6 px sous les lignes. */
  tag?: ReactNode;
  /** La valeur entière, quand la colonne la coupe (`maxWidth`) — dite en infobulle. */
  title?: string;
}

/**
 * Une action de ligne. Trois formes, une seule recette pour tous les tableaux de la Console : un bouton TEXTE
 * (`.btn-ghost.sm`), un bouton ICÔNE (`.btn-icon`, son libellé en infobulle), ou une PLACE GARDÉE.
 * ⚠️ La place gardée existe pour l'alignement : la dernière action d'une ligne (« Modifier ») tombe dans la même
 *    colonne d'une ligne à l'autre, même quand une ligne n'a pas d'action de sortie.
 */
export type DtAction =
  | {
    label: string;
    onClick: () => void;
    icon?: IconName;
    danger?: boolean;
    disabled?: boolean;
    /** Nom accessible quand le libellé seul ne dit pas QUELLE ligne : « Modifier » répété sur chaque ligne n'en désigne aucune. */
    name?: string;
  }
  | { spacer: true };

export interface DtRow {
  id: string;
  cells: Record<string, DtCell>;
  onClick?: () => void;
  /** Teinte de fond de ligne (ex. hors-ligne rosé — maquette). */
  tint?: string;
  /** Les boutons de la ligne, dans la colonne de fin. */
  actions?: DtAction[];
  /** Cellule de FIN libre (chevron…), quand la ligne n'a pas d'`actions`. */
  trailing?: ReactNode;
}

// La colonne d'actions COLLÉE au bord droit quand le tableau défile : son fond est OPAQUE (le papier, avec le voile
// de l'en-tête ou la teinte de la ligne posé dessus), sinon les cellules qui passent dessous se liraient au travers.
// ⚠️ Collée seulement quand le tableau DÉBORDE : au repos, une ombre de bord ne séparerait rien.
const STICKY_EDGE = 'inset 1px 0 0 var(--ink200), -12px 0 14px -12px rgba(2,6,23,.55)';
const stickyCell = (tint: string | undefined, z: number): CSSProperties => ({
  position: 'sticky', right: 0, zIndex: z, boxShadow: STICKY_EDGE,
  background: tint ? `linear-gradient(${tint}, ${tint}), var(--paper)` : 'var(--paper)',
});

/** Les boutons d'une ligne. ⚠️ Un bouton dans une ligne cliquable n'ouvre pas AUSSI la ligne. */
function RowActions({ actions }: { actions: DtAction[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      {actions.map((a, i) => {
        if ('spacer' in a) return <span key={i} aria-hidden="true" style={{ display: 'block', flexShrink: 0, width: 'var(--h-sm)', height: 'var(--h-sm)' }} />;
        const go = (e: MouseEvent) => { e.stopPropagation(); a.onClick(); };
        return a.icon
          ? (
            <button key={i} type="button" className={a.danger ? 'btn-icon danger' : 'btn-icon'} title={a.label} aria-label={a.name ?? a.label} disabled={a.disabled} onClick={go}>
              <Icon name={a.icon} size={15} />
            </button>
          )
          : (
            <button key={i} type="button" className={a.danger ? 'btn-ghost sm danger' : 'btn-ghost sm'} aria-label={a.name} disabled={a.disabled} onClick={go}>
              {a.label}
            </button>
          );
      })}
    </div>
  );
}

export interface DataTableProps {
  cols: DtCol[];
  rows: DtRow[];
  /** Unité du compteur (« client », « magasin », « dossier »). */
  unit: string;
  searchPlaceholder: string;
  pageSize?: number;
  emptyIcon: IconName;
  emptyTitle: string;
  emptyNote: ReactNode;
  /** Contenu optionnel de la barre de tête, à gauche (titre / chips). */
  headExtra?: ReactNode;
  /** Contenu optionnel de la barre de tête, à droite, juste avant la recherche (un segmenté de période). */
  headRight?: ReactNode;
  /**
   * Ligne à METTRE EN AVANT (résultat de la recherche transverse, `?device=…`).
   * ⚠️ Ouvre AUSSI la page qui la contient : surligner une ligne restée page 3 ne servirait à rien —
   * l'écran paraîtrait n'avoir rien trouvé alors qu'il a trouvé.
   */
  focusId?: string;
}

export function DataTable({ cols, rows, unit, searchPlaceholder, pageSize = 10, emptyIcon, emptyTitle, emptyNote, headExtra, headRight, focusId }: DataTableProps) {
  const ligneVisee = useRef<HTMLTableRowElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  // Le tableau déborde-t-il de sa carte ? C'est ce qui colle la colonne d'actions au bord.
  const [over, setOver] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [size, setSize] = useState(pageSize);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => cols.some((c) => (r.cells[c.key]?.text ?? '').toLowerCase().includes(needle)));
  }, [rows, cols, q]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const key = sort.key;
    return [...filtered].sort((a, b) => {
      const av = a.cells[key]?.sortValue ?? a.cells[key]?.text ?? '';
      const bv = b.cells[key]?.sortValue ?? b.cells[key]?.text ?? '';
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'fr');
      return cmp * sort.dir;
    });
  }, [filtered, sort]);

  const maxPage = Math.max(0, Math.ceil(sorted.length / size) - 1);
  const p = Math.min(page, maxPage);
  const shown = sorted.slice(p * size, p * size + size);
  const countLabel = q.trim()
    ? `${filtered.length} ${unit}${filtered.length > 1 ? 's' : ''} / ${rows.length}`
    : `${rows.length} ${unit}${rows.length > 1 ? 's' : ''}`;
  // ⚠️ La colonne d'actions ne compte pas dans le plancher : elle se dimensionne sur ses boutons.
  const tableMin = Math.max(520, cols.length * 116);
  const hasActions = rows.some((r) => r.actions !== undefined);
  const pinned = hasActions && over;

  const toggleSort = (key: string) => {
    setPage(0);
    setSort((s) => (s?.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));
  };

  // Ouvre la page qui contient la ligne visée, puis l'amène à l'écran. ⚠️ Sans le saut de page, une
  // ligne trouvée mais située page 3 resterait invisible et la recherche paraîtrait n'avoir rien donné.
  const rangVise = focusId ? sorted.findIndex((r) => r.id === focusId) : -1;
  useEffect(() => {
    if (rangVise >= 0) setPage(Math.floor(rangVise / size));
  }, [rangVise, size]);
  useEffect(() => {
    if (rangVise >= 0) ligneVisee.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [rangVise, p]);

  // Surveille le défileur ET son tableau : la colonne d'actions n'est collée que tant que le tableau déborde.
  // Sans `ResizeObserver` (environnement de test), rien n'est collé : le tableau reste lisible, il défile.
  const empty = shown.length === 0;
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => setOver(el.scrollWidth > el.clientWidth + 1);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    check();
    return () => ro.disconnect();
  }, [empty]);

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      {/* Barre de tête : slot libre · recherche à loupe · compteur mono (anatomie maquette). */}
      {/* La barre d'outils prend la géométrie de la charte (`TABLE.toolbar`, 11 / 14 — la maquette dit 11 de haut elle aussi). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: `${TABLE.toolbar.padV}px ${TABLE.toolbar.padH}px`, background: 'var(--head)', borderBottom: '1px solid var(--ink200)', flexWrap: 'wrap' }}>
        {headExtra}
        <div style={{ flex: 1 }} />
        {headRight}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink400)', display: 'grid' }}><Icon name="search" size={13} /></span>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder={searchPlaceholder}
            style={{ width: 190, height: 30, paddingLeft: 27, paddingRight: 10, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)' }}
          />
        </div>
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>{countLabel}</span>
      </div>

      {shown.length === 0 ? (
        q.trim() ? (
          <EmptyState
            icon="zoomOut"
            title={`Rien ne correspond à « ${q.trim()} »`}
            note="Vérifiez l'orthographe ou élargissez la recherche."
            action={<button className="ghost btn-ghost" style={{ width: 'auto', padding: '6px 12px' }} onClick={() => setQ('')}>Effacer la recherche</button>}
          />
        ) : (
          <EmptyState icon={emptyIcon} title={emptyTitle} note={emptyNote} />
        )
      ) : (
        <TableScroll minWidth={tableMin} scrollRef={scroller}>
            <thead>
              <tr>
                {cols.map((c) => {
                  const active = sort?.key === c.key;
                  // ⚠️ RIEN AUTOUR DU BOUTON DE TRI : c'est lui qui porte les 10 px (`.th-sort`). La règle `th` de la Console,
                  //    hors couche, battait le `th.sortable{padding:0}` de la charte — 6 px de plus de chaque côté, et l'en-tête
                  //    montait à 44 px contre 33 dans la maquette.
                  return (
                    <th key={c.key} className={c.sortable === false ? undefined : 'sortable'} style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', background: 'var(--ink-50)', padding: c.sortable === false ? '7px 10px' : 0 }}>
                      {c.sortable === false
                        ? c.label
                        : (
                          <button type="button" className={active ? 'th-sort active' : 'th-sort'} onClick={() => toggleSort(c.key)}>
                            {c.label}
                            {/* Le chevron reste VISIBLE au repos : c'est lui qui annonce qu'une colonne est triable. */}
                            <span className="arrow">{active ? (sort!.dir === 1 ? '▲' : '▼') : '⇅'}</span>
                          </button>
                        )}
                    </th>
                  );
                })}
                {/* La colonne de fin ne se trie pas : elle porte des boutons, pas une valeur. */}
                <th style={pinned ? stickyCell('var(--ink-50)', 2) : { background: 'var(--ink-50)' }} />
              </tr>
            </thead>
            <tbody>
              {/* ⚠️ `.dt-row` porte le survol et le filet de la charte en classe DÉDIÉE : un `<tr>` ne
                  peut pas être `display:flex`, la recette `.row-nav` ne lui va donc pas telle quelle
                  — seuls le filet et le survol la traversent. */}
              {shown.map((r) => (
                <tr
                  key={r.id}
                  className="dt-row"
                  ref={r.id === focusId ? ligneVisee : undefined}
                  onClick={r.onClick}
                  style={r.id === focusId
                    ? { cursor: r.onClick ? 'pointer' : undefined, background: 'var(--ok-bg)', boxShadow: 'inset 3px 0 0 var(--chart-ok)' }
                    : { cursor: r.onClick ? 'pointer' : undefined, background: r.tint }}
                >
                  {cols.map((c, i) => {
                    const cell = r.cells[c.key];
                    return (
                      <td key={c.key} style={{ padding: '9px 10px', verticalAlign: 'top', ...(c.minWidth ? { minWidth: c.minWidth } : null) }}>
                        {/* ⚠️ UNE LIGNE CLIQUABLE PORTE SON BOUTON dans la première cellule — le chemin du clavier, comme le
                            tableau partagé : le clic sur la ligne n'en est qu'un raccourci du pointeur. */}
                        {i === 0 && r.onClick ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); r.onClick!(); }}
                            style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', color: 'inherit', textAlign: 'inherit', cursor: 'pointer', borderRadius: 'var(--r-xs)' }}>
                            {cell?.v ?? '—'}
                          </button>
                        ) : c.maxWidth ? (
                          <div title={cell?.title} style={{ maxWidth: c.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell?.v ?? '—'}</div>
                        ) : (cell?.v ?? '—')}
                        {cell?.sub != null && (
                          <div className="mono" style={{ fontSize: 'var(--t-sub)', marginTop: 2, ...(c.wrap ? { whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: 1.45 } : { whiteSpace: 'nowrap' }),
                            ...(cell.subTone ? { color: `var(--${cell.subTone}-fg)`, fontWeight: 600 } : { color: 'var(--ink500)' }) }}>
                            {cell.sub}
                          </div>
                        )}
                        {cell?.sub2 != null && (
                          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.4, ...(c.wrap ? { whiteSpace: 'normal', overflowWrap: 'break-word' } : { whiteSpace: 'nowrap' }) }}>
                            {cell.sub2}
                          </div>
                        )}
                        {cell?.tag != null && <div style={{ marginTop: 6, lineHeight: 1 }}>{cell.tag}</div>}
                      </td>
                    );
                  })}
                  {r.actions !== undefined ? (
                    <td style={{ padding: '9px 10px', verticalAlign: 'middle', width: '1%', whiteSpace: 'nowrap',
                      ...(pinned ? stickyCell(r.id === focusId ? 'var(--ok-bg)' : r.tint, 1) : null) }}>
                      <RowActions actions={r.actions} />
                    </td>
                  ) : (
                    <td style={{ textAlign: 'right', padding: '9px 10px' }}>{r.trailing}</td>
                  )}
                </tr>
              ))}
            </tbody>
        </TableScroll>
      )}

      {/* Pied : plage · sélecteur de taille · pagination (maquette).
          ⚠️ Il porte le MÊME voile que les en-têtes de colonnes (`--ink-50`) et le même filet
          (`--ink-200`). Sans fond, il se confondait avec la dernière ligne et le tableau n'avait plus
          de bas : on ne voyait plus où la liste s'arrêtait. */}
      {sorted.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink200)', flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{p * size + 1}–{Math.min((p + 1) * size, sorted.length)} sur {sorted.length}</span>
          <div style={{ flex: 1 }} />
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>lignes</span>
          {[10, 25, 50].map((n) => (
            <button key={n} type="button" onClick={() => { setSize(n); setPage(0); }}
              className={size === n ? 'seg active' : 'seg'}>
              {n}
            </button>
          ))}
          <button type="button" disabled={p === 0} onClick={() => setPage(p - 1)}
            className="pg-btn">←</button>
          <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{p + 1} / {maxPage + 1}</span>
          <button type="button" disabled={p >= maxPage} onClick={() => setPage(p + 1)}
            className="pg-btn">→</button>
        </div>
      )}
    </div>
  );
}
